import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertTriangle, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_90b4c8a6-2372-4e9e-bd52-ac51039d9d6b/artifacts/l6uwsqcm_logo_bravo_cinemastreaming.png";

const CinemaResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Enlace inválido. No se ha proporcionado un token de recuperación.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinemas/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        toast.success('Contraseña actualizada correctamente');
      } else {
        setError(data.detail || 'Error al restablecer la contraseña');
      }
    } catch {
      setError('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="mb-8">
          <Link to="/cinema/login" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-5 h-5" /><span>Volver al login</span>
          </Link>
          <img src={LOGO_URL} alt="Bravo Cinemastreaming" className="h-12 w-auto mb-6" />
          <h1 className="text-white text-2xl font-bold mb-2">Restablecer contraseña</h1>
          <p className="text-white/60">Introduce tu nueva contraseña para acceder a tu cuenta.</p>
        </div>

        <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
          {success ? (
            <div className="text-center py-6" data-testid="reset-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-white text-xl font-bold mb-2">Contraseña actualizada</h3>
              <p className="text-white/60 mb-6">Tu contraseña ha sido restablecida correctamente. Ya puedes iniciar sesión con tu nueva contraseña.</p>
              <Link
                to="/cinema/login"
                className="inline-block px-6 py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] transition-colors"
                data-testid="go-to-login-btn"
              >
                Ir al login
              </Link>
            </div>
          ) : !token ? (
            <div className="text-center py-6" data-testid="reset-invalid-token">
              <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h3 className="text-white text-xl font-bold mb-2">Enlace inválido</h3>
              <p className="text-white/60 mb-6">{error}</p>
              <Link
                to="/cinema/login"
                className="inline-block px-6 py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] transition-colors"
              >
                Volver al login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="reset-error">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-white/60 text-sm mb-2">Nueva contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-11 pr-11 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#e4002b]"
                    placeholder="Mínimo 6 caracteres"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Confirmar contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#e4002b]"
                    placeholder="Repite tu contraseña"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Actualizando...</>
                ) : (
                  'Restablecer contraseña'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CinemaResetPasswordPage;
